import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import PlayerAvatar from "@/components/app/PlayerAvatar";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Crown, Trophy } from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1553689895-0caa4124e237?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHw0fHxiYXNrZXRiYWxsJTIwY291cnQlMjBkYXJrJTIwbW9vZHl8ZW58MHx8fHwxNzgyNjA2Njg5fDA&ixlib=rb-4.1.0&q=85";

const CATEGORIES = [
  { key: "ppg", label: "Points Per Game", suffix: "" },
  { key: "reb_avg", label: "Rebounds Per Game", suffix: "" },
  { key: "ast_avg", label: "Assists Per Game", suffix: "" },
  { key: "stl_avg", label: "Steals Per Game", suffix: "" },
  { key: "blk_avg", label: "Blocks Per Game", suffix: "" },
];

export default function Leaders() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/leaders").then((r) => setData(r.data)); }, []);

  if (!data) return <div className="p-10 text-ink-300">Loading leaderboards…</div>;

  return (
    <div>
      <div className="relative h-48 md:h-56 overflow-hidden">
        <img src={HERO} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-b from-ink-950/30 to-ink-950" />
        <div className="relative h-full px-6 md:px-12 flex flex-col justify-end pb-6">
          <p className="eyebrow">NPHS Canucks Record Book</p>
          <h1 className="heading-1 flex items-center gap-3">
            <Crown className="w-10 h-10 text-burgundy" /> All-Time <span className="text-burgundy">Leaders</span>
          </h1>
        </div>
      </div>

      <div className="px-6 md:px-12 py-8">
        <Tabs defaultValue="ppg">
          <TabsList className="bg-ink-900 border border-ink-700/60">
            {CATEGORIES.map((c) => (
              <TabsTrigger
                key={c.key}
                value={c.key}
                className="data-[state=active]:bg-burgundy data-[state=active]:text-white uppercase tracking-wider text-xs px-4"
                data-testid={`leaders-tab-${c.key}`}
              >
                {c.label}
              </TabsTrigger>
            ))}
          </TabsList>

          {CATEGORIES.map((c, idx) => (
            <TabsContent key={c.key} value={c.key} className="mt-6">
              <div className="bg-ink-800 rounded-lg border border-white/5 overflow-hidden">
                {(data[c.key] || []).length === 0 ? (
                  <p className="text-ink-400 text-sm p-6 text-center">No stats yet.</p>
                ) : (
                  data[c.key].map((row, i) => {
                    const top = i === 0;
                    const p = row.player;
                    return (
                      <Link
                        key={p.id}
                        to={`/player/${p.slug}`}
                        className={`flex items-center gap-4 px-4 py-3 border-b border-ink-700/30 last:border-0 hover:bg-ink-900/40 transition ${top ? "bg-burgundy/15" : ""}`}
                        data-testid={`leader-row-${c.key}-${i}`}
                      >
                        <div className={`w-10 text-center font-display text-2xl font-black ${top ? "text-burgundy" : "text-ink-400"}`}>
                          {top ? <Trophy className="w-5 h-5 inline" /> : `#${i + 1}`}
                        </div>
                        <PlayerAvatar player={p} size="md" />
                        <div className="flex-1 min-w-0">
                          <p className="font-display text-lg font-bold uppercase">{p.player_name}</p>
                          <p className="text-xs text-ink-400">#{p.jersey_number || "—"} · {p.position || "—"} · {p.status === "active" ? (
                            <span className="text-green-400 uppercase tracking-wider">Active</span>
                          ) : (
                            <span className="text-ink-300">Class of {p.graduation_year || "—"}</span>
                          )}</p>
                        </div>
                        <div className="text-right">
                          <p className="font-display text-3xl font-black">{row[c.key]}{c.suffix}</p>
                          <p className="eyebrow text-[9px]">{row.gp} GP</p>
                        </div>
                      </Link>
                    );
                  })
                )}
              </div>
            </TabsContent>
          ))}
        </Tabs>
      </div>
    </div>
  );
}
